import React, { useState } from "react";
import { ShieldCheck, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const BillingAuditCard = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (apply) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing/audit", null, { params: { days: 60, apply } });
      setResult(data);
      if (apply) toast.success(`Refunded $${data.total_refund.toFixed(2)} across ${data.overcharged} call${data.overcharged === 1 ? "" : "s"}`);
      else if (!data.overcharged) toast.success("No overcharged calls found in the last 60 days");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm mb-6" data-testid="billing-audit-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Billing audit</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Checks every charged call (last 60 days) against Twilio&rsquo;s destination leg and refunds any time that was
            billed but never connected (ringing, no-answer, hung up before pick-up).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => run(false)} disabled={busy} data-testid="billing-audit-scan"
            className="h-10 px-4 rounded-full border border-black/10 text-sm font-bold hover:border-primary/40 disabled:opacity-60 flex items-center gap-1.5">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Scan
          </button>
          {result?.overcharged > 0 && !result.applied && (
            <button onClick={() => run(true)} disabled={busy} data-testid="billing-audit-refund"
              className="h-10 px-4 rounded-full bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5">
              <Undo2 className="h-4 w-4" /> Refund ${result.total_refund.toFixed(2)}
            </button>
          )}
        </div>
      </div>
      {result && (
        <div className="mt-4 text-sm" data-testid="billing-audit-result">
          <div className="text-zinc-600">
            Scanned <b>{result.scanned}</b> charged calls · <b>{result.overcharged}</b> overcharged ·
            {" "}{result.applied ? "refunded" : "refund due"} <b>${result.total_refund.toFixed(2)}</b>
          </div>
          {result.findings.length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-black/5 border border-black/5 rounded-xl">
              {result.findings.map((f) => (
                <div key={f.call_id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`audit-row-${f.call_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{f.country} · {f.number}</div>
                    <div className="text-zinc-400">{new Date(f.started_at).toLocaleString()} · connected {f.connected_seconds}s · billed {f.billed_minutes} min</div>
                  </div>
                  <div className="font-mono font-bold text-emerald-700 shrink-0">+${f.refund.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
